import { OnsiteAccountLocation, Organizer } from '../../organizers/types';
import { deleteRequest, getRequest, getWithParamsRequest, patchRequest, postRequest } from '../utils/provider';
import { ApiResponse } from '../utils/responseTransform';

interface OrganizerRole {
  label: string;
  description: string;
  value: string;
}

export interface JobTitleOption {
  label: string;
  value: string;
  group: string;
}

interface OrganizerInfoPayload {
  firstName: string;
  lastName: string;
  email: string;
  mobilePhoneNumber: string;
  roles: string[];
}

export type ValidationErrors = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  roles: string;
};
export interface AdminOrganizer {
  name: string;
  email: string;
  id: number;
  uuid: string;
}

export async function getOrganizer(uuid: string, signal?: AbortSignal): Promise<ApiResponse<{ organizer: Organizer }>> {
  return getRequest(`organizers/${uuid}`, signal);
}

export async function createOrganizer(payload: OrganizerInfoPayload): Promise<ApiResponse<{ organizer: Organizer }>> {
  return postRequest('organizers/create_organizer', payload);
}

export async function updateOrganizer(
  uuid: string,
  payload: OrganizerInfoPayload
): Promise<ApiResponse<{ organizer: Organizer }>> {
  return patchRequest(`organizers/${uuid}/update_organizer`, payload);
}

export async function resendInvite(uuid: string, signal?: AbortSignal): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`organizers/${uuid}/reinvite`, {}, signal);
}

export async function resetPassword(uuid: string, signal?: AbortSignal): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`organizers/${uuid}/reset_password`, {}, signal);
}

export async function deactivateOrganizer(
  uuid: string,
  signal?: AbortSignal
): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`organizers/${uuid}/deactivate`, {}, signal);
}

export async function approveOrganizer(uuid: string, signal?: AbortSignal): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`organizers/${uuid}/approve`, {}, signal);
}

export async function rejectOrganizer(uuid: string, signal?: AbortSignal): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`organizers/${uuid}/reject`, {}, signal);
}

export async function getRoles(signal?: AbortSignal): Promise<ApiResponse<{ roles: OrganizerRole[] }>> {
  return getRequest('organizers/roles', signal);
}

export async function getJobTitles(signal?: AbortSignal): Promise<ApiResponse<{ jobTitles: JobTitleOption[] }>> {
  return getRequest('organizers/job_titles', signal);
}

export async function getOrganizers(
  params: {
    perPage: number;
    page: number;
    q: string | null;
    withRoles?: string[];
    withoutRoles?: string[];
    deactivated?: boolean | null;
    active?: boolean | null;
    rejected?: boolean | null;
    defaultAccountLocationId?: string | null;
  },
  signal?: AbortSignal
): Promise<ApiResponse<{ organizers: Organizer[] }>> {
  return getWithParamsRequest('organizers', params, signal);
}

export async function getOrganizerAccountLocations(
  uuid: string,
  signal?: AbortSignal
): Promise<ApiResponse<{ accountLocations: Array<OnsiteAccountLocation> }>> {
  return getRequest(`organizers/${uuid}/account_locations`, signal);
}

export async function addOrganizerAccountLocation(
  uuid: string,
  locationId: string,
  signal?: AbortSignal
): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`organizers/${uuid}/account_locations`, { id: locationId }, signal);
}

export async function deleteOrganizerAccountLocation(
  uuid: string,
  locationId: string
): Promise<ApiResponse<{ message: string }>> {
  return deleteRequest(`organizers/${uuid}/account_locations/${locationId}`);
}

export async function getSharedBillingOrganizers(
  id,
  signal?: AbortSignal
): Promise<ApiResponse<{ adminOrganizers: AdminOrganizer[] }>> {
  return getRequest(`accounts/${id}/admin_organizers`, signal);
}

export async function getAccountLocationsByConfirmationToken(
  params: { perPage: number; page: number; q: string | null; confirmationToken: string },
  signal?: AbortSignal
): Promise<ApiResponse<Pick<AccountLocation, 'id' | 'name' | 'address'>[]>> {
  return getWithParamsRequest('organizers/account_locations', params, signal);
}

export async function reactivateOrganizer(
  uuid: string,
  signal?: AbortSignal
): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`organizers/${uuid}/reactivate`, {}, signal);
}
